<template>
  <!-- Reconciliation Details Modal -->
  <div v-if="show" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header text-white">
          <h5 class="modal-title">
            <i class="bx bx-file me-2"></i>
            Bank Reconciliation Details
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="$emit('close')"></button>
        </div>

        <!-- Modal Body -->
        <div class="modal-body">
          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>

          <div v-else>
            <!-- Reconciliation Summary -->
            <div class="row mb-4">
              <div class="col-lg-8">
                <div class="card bg-light">
                  <div class="card-body">
                    <h6 class="text-muted mb-3">Reconciliation Information</h6>
                    <div class="row">
                      <div class="col-md-6 mb-3">
                        <label class="text-muted small">Document Name</label>
                        <h6 class="mb-0">{{ reconciliationDetails.reconciliation.document_name }}</h6>
                      </div>
                      <div class="col-md-6 mb-3">
                        <label class="text-muted small">Period</label>
                        <h6 class="mb-0">{{ formatPeriod(reconciliationDetails.reconciliation.period) }}</h6>
                      </div>
                      <div class="col-md-6 mb-3">
                        <label class="text-muted small">Bank Source</label>
                        <h6 class="mb-0">{{ reconciliationDetails.reconciliation.bank_source }}</h6>
                      </div>
                      <div class="col-md-6 mb-3">
                        <label class="text-muted small">Bank Account</label>
                        <h6 class="mb-0">{{ reconciliationDetails.reconciliation.bank_account || '-' }}</h6>
                      </div>
                      <div class="col-md-6">
                        <label class="text-muted small">Uploaded By</label>
                        <h6 class="mb-0">{{ reconciliationDetails.reconciliation.uploaded_by }}</h6>
                      </div>
                      <div class="col-md-6">
                        <label class="text-muted small">Upload Date</label>
                        <h6 class="mb-0">{{ formatDateTime(reconciliationDetails.reconciliation.upload_date) }}</h6>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-lg-4">
                <div class="card">
                  <div class="card-body">
                    <h6 class="text-muted mb-3">Reconciliation Statistics</h6>

                    <!-- Match Rate Progress -->
                    <div class="mb-4">
                      <div class="d-flex justify-content-between mb-2">
                        <span>Match Rate</span>
                        <span class="fw-bold">{{ reconciliationDetails.summary.matchRate }}%</span>
                      </div>
                      <div class="progress" style="height: 20px;">
                        <div class="progress-bar" role="progressbar"
                             :style="{width: reconciliationDetails.summary.matchRate + '%'}"
                             :class="getProgressBarClass(reconciliationDetails.summary.matchRate)">
                        </div>
                      </div>
                    </div>

                    <div class="row text-center">
                      <div class="col-6">
                        <h4 class="text-success mb-0">{{ reconciliationDetails.summary.matchedCount }}</h4>
                        <small class="text-muted">Matched</small>
                      </div>
                      <div class="col-6">
                        <h4 class="text-danger mb-0">{{ reconciliationDetails.summary.unmatchedCount }}</h4>
                        <small class="text-muted">Unmatched</small>
                      </div>
                    </div>

                    <hr>

                    <div>
                      <small class="text-muted">Total Amount</small>
                      <h5 class="mb-0">ETB {{ formatAmount(reconciliationDetails.reconciliation.total_amount) }}</h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Tabs for Matched/Unmatched -->
            <ul class="nav nav-tabs" role="tablist">
              <li class="nav-item">
                <a class="nav-link active" data-bs-toggle="tab" href="#matched" role="tab">
                  <i class="bx bx-check-circle text-success me-1"></i>
                  Matched Transactions ({{ reconciliationDetails.transactions.matched.length }})
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" data-bs-toggle="tab" href="#unmatched" role="tab">
                  <i class="bx bx-x-circle text-danger me-1"></i>
                  Unmatched Transactions ({{ reconciliationDetails.transactions.unmatched.length }})
                </a>
              </li>
            </ul>

            <div class="tab-content p-3 border border-top-0">
              <!-- Matched Transactions -->
              <div class="tab-pane active" id="matched" role="tabpanel">
                <div class="table-responsive">
                  <table class="table table-hover mb-0">
                    <thead class="table-light">
                    <tr>
                      <th>Transaction Date</th>
                      <th>Bank Reference</th>
                      <th>Matched With</th>
                      <th>Hospital</th>
                      <th>Match Type</th>
                      <th class="text-end">Amount</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="trans in reconciliationDetails.transactions.matched" :key="trans.detail_id">
                      <td>{{ formatDate(trans.transaction_date) }}</td>
                      <td>{{ trans.bank_reference }}</td>
                      <td>
                        <span class="badge bg-primary">{{ trans.batch_number || '-' }}</span>
                      </td>
                      <td>{{ trans.hospital_name || '-' }}</td>
                      <td>
                        <span :class="getMatchTypeBadge(trans.match_type)">
                          {{ trans.match_type }}
                        </span>
                        <small v-if="trans.match_confidence" class="text-muted">
                          ({{ trans.match_confidence }}%)
                        </small>
                      </td>
                      <td class="text-end fw-bold">ETB {{ formatAmount(trans.amount) }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Unmatched Transactions -->
              <div class="tab-pane" id="unmatched" role="tabpanel">
                <div v-if="reconciliationDetails.transactions.unmatched.length > 0"
                     class="alert alert-warning mb-3">
                  <i class="bx bx-info-circle me-2"></i>
                  These transactions could not be automatically matched. You can manually match them to settlement batches.
                </div>

                <div class="table-responsive">
                  <table class="table table-hover mb-0">
                    <thead class="table-light">
                    <tr>
                      <th>Transaction Date</th>
                      <th>Bank Reference</th>
                      <th>Description</th>
                      <th class="text-end">Amount</th>
                      <th class="text-center">Action</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="trans in reconciliationDetails.transactions.unmatched" :key="trans.detail_id">
                      <td>{{ formatDate(trans.transaction_date) }}</td>
                      <td>{{ trans.bank_reference }}</td>
                      <td>{{ trans.description || '-' }}</td>
                      <td class="text-end fw-bold">ETB {{ formatAmount(trans.amount) }}</td>
                      <td class="text-center">
                        <button @click="openManualMatch(trans)" class="btn btn-sm btn-primary">
                          <i class="bx bx-link"></i> Manual Match
                        </button>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer bg-light">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            Close
          </button>
          <button @click="exportReconciliation" class="btn btn-primary">
            <i class="bx bx-download me-1"></i> Export CSV
          </button>
        </div>
      </div>
    </div>

    <!-- Manual Match Dialog -->
    <div v-if="showManualMatch" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.7)">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Manual Transaction Matching</h5>
            <button type="button" class="btn-close" @click="closeManualMatch"></button>
          </div>
          <div class="modal-body">
            <!-- Transaction Details -->
            <div class="card bg-light mb-3">
              <div class="card-body">
                <h6 class="card-title">Transaction Details</h6>
                <div class="row">
                  <div class="col-md-4">
                    <small class="text-muted">Date</small>
                    <p class="mb-0">{{ formatDate(selectedTransaction.transaction_date) }}</p>
                  </div>
                  <div class="col-md-4">
                    <small class="text-muted">Reference</small>
                    <p class="mb-0">{{ selectedTransaction.bank_reference }}</p>
                  </div>
                  <div class="col-md-4">
                    <small class="text-muted">Amount</small>
                    <p class="mb-0 fw-bold">ETB {{ formatAmount(selectedTransaction.amount) }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Search for Settlement -->
            <div class="mb-3">
              <label class="form-label">Search Settlement Batch</label>
              <input v-model="searchBatch" type="text" class="form-control"
                     placeholder="Search by batch number, hospital name..."
                     @input="searchSettlements">
            </div>

            <!-- Matching Settlements -->
            <div class="table-responsive" style="max-height: 300px; overflow-y: auto;">
              <table class="table table-hover">
                <thead class="table-light sticky-top">
                <tr>
                  <th>Batch Number</th>
                  <th>Hospital</th>
                  <th>Date</th>
                  <th class="text-end">Amount</th>
                  <th>Match</th>
                </tr>
                </thead>
                <tbody>
                <tr v-for="batch in matchingBatches" :key="batch.batch_id"
                    :class="{'table-warning': isAmountClose(batch.settled_amount, selectedTransaction.amount)}">
                  <td>{{ batch.batch_number }}</td>
                  <td>{{ batch.hospital_name }}</td>
                  <td>{{ formatDate(batch.settlement_date) }}</td>
                  <td class="text-end">
                    ETB {{ formatAmount(batch.settled_amount) }}
                    <small v-if="batch.settled_amount !== selectedTransaction.amount"
                           class="text-muted d-block">
                      Diff: {{ formatAmount(Math.abs(batch.settled_amount - selectedTransaction.amount)) }}
                    </small>
                  </td>
                  <td>
                    <button @click="confirmMatch(batch)" class="btn btn-sm btn-success">
                      <i class="bx bx-check"></i>
                    </button>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>

            <!-- Notes -->
            <div class="mt-3">
              <label class="form-label">Notes (Optional)</label>
              <textarea v-model="matchNotes" class="form-control" rows="2"
                        placeholder="Add any notes about this manual match..."></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeManualMatch">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { authUrl, executeJson } from "@/api";

export default {
  name: "ReconciliationDetailsModal",
  props: {
    reconciliation: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      reconciliationDetails: {
        reconciliation: {},
        transactions: {
          matched: [],
          unmatched: []
        },
        summary: {
          totalTransactions: 0,
          matchedCount: 0,
          unmatchedCount: 0,
          matchRate: 0
        }
      },
      showManualMatch: false,
      selectedTransaction: null,
      searchBatch: '',
      matchingBatches: [],
      matchNotes: ''
    };
  },

  computed: {
    show() {
      return this.reconciliation != null;
    }
  },

  watch: {
    reconciliation(newVal) {
      if (newVal) {
        this.loadReconciliationDetails();
      }
    }
  },

  mounted() {
    if (this.reconciliation) {
      this.loadReconciliationDetails();
    }
  },

  methods: {
    async loadReconciliationDetails() {
      this.loading = true;

      const data = {
        "function": "getReconciliationDetails",
        "reconciliationId": this.reconciliation.reconciliation_id
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.reconciliationDetails = res.data.data;
        }
      } catch (e) {
        console.error("Failed to load reconciliation details:", e);
      } finally {
        this.loading = false;
      }
    },

    openManualMatch(transaction) {
      this.selectedTransaction = transaction;
      this.showManualMatch = true;
      this.searchSettlements();
    },

    closeManualMatch() {
      this.showManualMatch = false;
      this.selectedTransaction = null;
      this.searchBatch = '';
      this.matchingBatches = [];
      this.matchNotes = '';
    },

    async searchSettlements() {
      const data = {
        "function": "getUnreconciledSettlements",
        "searchTerm": this.searchBatch,
        "startDate": this.selectedTransaction.transaction_date,
        "endDate": this.selectedTransaction.transaction_date
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.matchingBatches = res.data.data.settlements || [];
        }
      } catch (e) {
        console.error("Failed to search settlements:", e);
      }
    },

    isAmountClose(amount1, amount2) {
      const diff = Math.abs(amount1 - amount2);
      const threshold = Math.max(amount1, amount2) * 0.01; // 1% threshold
      return diff <= threshold;
    },

    async confirmMatch(batch) {
      if (!confirm(`Match this transaction with batch ${batch.batch_number}?`)) return;

      const data = {
        "function": "manualMatchTransaction",
        "detailId": this.selectedTransaction.detail_id,
        "batchId": batch.batch_id,
        "notes": this.matchNotes
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          alert('Transaction matched successfully');
          this.closeManualMatch();
          await this.loadReconciliationDetails();
        } else {
          alert(res.data.message || 'Failed to match transaction');
        }
      } catch (e) {
        console.error("Failed to match transaction:", e);
        alert('Failed to match transaction');
      }
    },

    async exportReconciliation() {
      if (!this.reconciliationDetails.reconciliation) return;

      try {
        let csvContent = '';
        const recon = this.reconciliationDetails.reconciliation;

        // Add report header
        csvContent += 'Bank Reconciliation Report\n';
        csvContent += `Document: ${recon.document_name}\n`;
        csvContent += `Period: ${this.formatPeriod(recon.period)}\n`;
        csvContent += `Bank: ${recon.bank_source}\n`;
        csvContent += `Account: ${recon.bank_account || 'N/A'}\n`;
        csvContent += `Generated: ${new Date().toLocaleString()}\n\n`;

        // Add summary section
        csvContent += 'SUMMARY\n';
        csvContent += 'Metric,Value\n';
        csvContent += `Total Transactions,${recon.total_records}\n`;
        csvContent += `Matched Transactions,${recon.matched_records}\n`;
        csvContent += `Unmatched Transactions,${recon.unmatched_records}\n`;
        csvContent += `Total Amount,"${this.formatAmount(recon.total_amount)}"\n`;
        csvContent += `Matched Amount,"${this.formatAmount(recon.matched_amount)}"\n`;
        csvContent += `Match Rate,${this.reconciliationDetails.summary.matchRate}%\n`;
        csvContent += `Upload Date,"${this.formatDateTime(recon.upload_date)}"\n`;
        csvContent += `Uploaded By,${recon.uploaded_by}\n\n`;

        // Add matched transactions
        if (this.reconciliationDetails.transactions.matched.length > 0) {
          csvContent += 'MATCHED TRANSACTIONS\n';
          csvContent += 'Transaction Date,Bank Reference,Matched With,Hospital,Match Type,Confidence,Amount\n';

          this.reconciliationDetails.transactions.matched.forEach(trans => {
            csvContent += `${this.formatDate(trans.transaction_date)},`;
            csvContent += `"${trans.bank_reference}",`;
            csvContent += `"${trans.batch_number || '-'}",`;
            csvContent += `"${trans.hospital_name || '-'}",`;
            csvContent += `${trans.match_type},`;
            csvContent += `${trans.match_confidence || '-'}%,`;
            csvContent += `"${this.formatAmount(trans.amount)}"\n`;
          });

          // Add total for matched
          const matchedTotal = this.reconciliationDetails.transactions.matched.reduce(
              (sum, trans) => sum + parseFloat(trans.amount || 0), 0
          );
          csvContent += `,,,,,Total,"${this.formatAmount(matchedTotal)}"\n\n`;
        }

        // Add unmatched transactions
        if (this.reconciliationDetails.transactions.unmatched.length > 0) {
          csvContent += 'UNMATCHED TRANSACTIONS\n';
          csvContent += 'Transaction Date,Bank Reference,Description,Amount\n';

          this.reconciliationDetails.transactions.unmatched.forEach(trans => {
            csvContent += `${this.formatDate(trans.transaction_date)},`;
            csvContent += `"${trans.bank_reference}",`;
            csvContent += `"${trans.description || '-'}",`;
            csvContent += `"${this.formatAmount(trans.amount)}"\n`;
          });

          // Add total for unmatched
          const unmatchedTotal = this.reconciliationDetails.transactions.unmatched.reduce(
              (sum, trans) => sum + parseFloat(trans.amount || 0), 0
          );
          csvContent += `,,,Total,"${this.formatAmount(unmatchedTotal)}"\n`;
        }

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        // Generate filename
        const filename = `bank_reconciliation_${recon.period}_${new Date().getTime()}.csv`;

        link.download = filename;
        link.href = url;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 100);

      } catch (e) {
        console.error("Failed to export reconciliation:", e);
        alert('Failed to export reconciliation report');
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatDateTime(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatPeriod(period) {
      if (!period) return '-';
      // Handle YYYY-MM format
      if (period.includes('-') && period.length === 7) {
        const [year, month] = period.split('-');
        const date = new Date(year, month - 1);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      }
      // Handle full date format
      const date = new Date(period);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    },

    formatAmount(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    },

    getProgressBarClass(rate) {
      if (rate >= 90) return 'bg-success';
      if (rate >= 70) return 'bg-warning';
      return 'bg-danger';
    },

    getMatchTypeBadge(type) {
      const badges = {
        'exact': 'badge bg-success',
        'fuzzy': 'badge bg-warning',
        'manual': 'badge bg-info'
      };
      return badges[type] || 'badge bg-secondary';
    }
  }
};
</script>

<style scoped>
.modal.show {
  display: block !important;
}

.btn-close-white {
  filter: brightness(0) invert(1);
}

.nav-tabs .nav-link {
  color: #495057;
}

.nav-tabs .nav-link.active {
  color: #0dcaf0;
  border-color: #dee2e6 #dee2e6 #fff;
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f8f9fa;
}

.table-warning {
  background-color: rgba(255, 193, 7, 0.1);
}
</style>